import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Eye } from 'lucide-react';
import './candidate-status-list.css';
import { getCandidatesByStatus } from '../../../services/admin/api';

const STATUS_MAP = {
  READY: 'READY_FOR_BGC',
  WIP: 'WORK_IN_PROGRESS',
  VERIFIED: 'VERIFIED',
  COMPLETED: 'COMPLETED',
};

const STATUS_OPTIONS = [
  { label: "Ready for BGC", value: "READY" },
  { label: "Work In Progress", value: "WIP" },
  { label: "Verified", value: "VERIFIED" },
  { label: "Completed", value: "COMPLETED" }
];

const CandidateStatusList = () => {
  const { status } = useParams();
  const navigate = useNavigate();

  const [selectedStatus, setSelectedStatus] = useState(status || "WIP");
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  const [sortColumn, setSortColumn] = useState('candidateId');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isAsc, setIsAsc] = useState(true);

  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (status) setSelectedStatus(status);
  }, [status]);

  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        setLoading(true);
        const backendStatus = STATUS_MAP[selectedStatus] || selectedStatus;
        const response = await getCandidatesByStatus(backendStatus);

        if (Array.isArray(response?.data)) {
          setCandidates(response.data);
        } else {
          setCandidates([]);
        }
      } catch (error) {
        console.error("Error fetching candidates:", error);
        setCandidates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, [selectedStatus]);

  // handle the sort with consistency icons and defualt sort asc . 
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Same column -> toggle
      const newIsAsc = !isAsc;
      setIsAsc(newIsAsc);
      setSortOrder(newIsAsc ? "asc" : "desc");
    } else {
      // Different column -> default to ascending
      setSortColumn(column);
      setIsAsc(true);
      setSortOrder("asc");
    }

    setCurrentPage(0);
  };


  const filteredCandidates = useMemo(() => {
    return candidates
      .filter((c) =>
        (c.candidateId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.fullName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.bgcStatus || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.clientName || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        const aRaw = (a[sortColumn] || '').toString();
        const bRaw = (b[sortColumn] || '').toString();

        const aLower = aRaw.toLowerCase();
        const bLower = bRaw.toLowerCase();

        if (aLower < bLower) return sortOrder === 'asc' ? -1 : 1;
        if (aLower > bLower) return sortOrder === 'asc' ? 1 : -1;

        if (aRaw < bRaw) return sortOrder === 'asc' ? -1 : 1;
        if (aRaw > bRaw) return sortOrder === 'asc' ? 1 : -1;

        return 0;
      });
  }, [searchTerm, sortColumn, sortOrder, candidates]);

  const paginatedCandidates = useMemo(() => {
    const start = currentPage * itemsPerPage;
    return filteredCandidates.slice(start, start + itemsPerPage);
  }, [filteredCandidates, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);

  if (loading) {
    return <div className="loading-message">Loading candidates...</div>;
  }

  const renderSortArrow = (field) => {
    const isActive = sortColumn === field;

    return (
      <span
        className={`sort-icons ${isActive ? "sort-active" : ""
          }`}
      >
        {/* showing the next action button  */}
        {!isActive
          ? "▲▼"
          : sortOrder === "asc"
            ? "▼" : "▲"
        }
      </span>
    );
  };

  return (
    <div className="candidateStatus-wrapper">
      <div className="candidateStatus-table-controls">
        <div className="candidateStatus-entries-select">
          Show{" "}
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries
        </div>

        <div className="status-filter-container">
          <label className="status-filter-label">Filter by Status</label>
          <select
            className="status-filter-dropdown"
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              setCurrentPage(0);
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="candidateStatus-search-box">
          <div className="candidateStatus-search-input-wrapper">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0);
              }}
            />
            <Search className="candidateStatus-search-icon" size={18} />
          </div>
        </div>
      </div>

      <div className="candidateStatus-table-container">
        <table className="candidateStatus-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('candidateId')}>
                Candidate ID {renderSortArrow('candidateId')}
              </th>
              <th onClick={() => handleSort('fullName')}>
                Name {renderSortArrow('fullName')}
              </th>
              <th onClick={() => handleSort('email')}>
                Email {renderSortArrow('email')}
              </th>
              <th onClick={() => handleSort('bgcStatus')}>
                BGC Status {renderSortArrow('bgcStatus')}
              </th>
              <th onClick={() => handleSort('clientName')}>
                Client Name {renderSortArrow('clientName')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {paginatedCandidates.length > 0 ? (
              paginatedCandidates.map((item, index) => (
                <tr key={index}>
                  <td>{item.candidateId}</td>
                  <td>{item.fullName}</td>
                  <td>{item.email}</td>
                  <td>{item.bgcStatus}</td>
                  <td>{item.clientName}</td>
                  <td className="action-icons">
                    <button
                      className="icon-btn"
                      title="View Candidate Details"
                      onClick={() =>
                        navigate(`/admin/candidate-details/${item.candidateId}`, {
                          state: { candidate: item }
                        })
                      }
                    >
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="candidateStatus-no-data">
                  No matching records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="candidateStatus-pagination">
        <div className="candidateStatus-pagination-summary">
          Showing {filteredCandidates.length === 0 ? 0 : currentPage * itemsPerPage + 1} to{" "}
          {Math.min((currentPage + 1) * itemsPerPage, filteredCandidates.length)} of{" "}
          {filteredCandidates.length} entries
        </div>

        <div className="candidateStatus-pagination-buttons">
          <button
            className="candidateStatus-page-btn"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
            disabled={currentPage === 0}
          >
            ←
          </button>

          <button className="candidateStatus-page-btn active">
            {currentPage + 1}
          </button>

          <button
            className="candidateStatus-page-btn"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
            disabled={currentPage >= totalPages - 1 || totalPages === 0}
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
};

export default CandidateStatusList;
